<template>
  <div class="gallery-permission-editor">
    <n-form :model="permissionsForm" label-placement="left" label-width="120">
      <n-form-item label="Gallery">
        <div class="gallery-info">
          <strong>{{ gallery.name }}</strong>
          <n-tag v-if="gallery.isDefault" type="info" size="small">Default</n-tag>
          <n-tag v-if="gallery.isPrivate" type="warning" size="small">Private</n-tag>
        </div>
      </n-form-item>

      <n-divider title-placement="left">Permissions</n-divider>

      <div class="permissions-list">
        <div
          v-for="(permission, index) in permissionsForm.permissions"
          :key="index"
          class="permission-item"
        >
          <n-card size="small">
            <div class="permission-header">
              <n-select
                v-model:value="permission.type"
                :options="permissionTypeOptions"
                placeholder="Permission Type"
                style="flex: 1; margin-right: 8px"
              />
              <n-select
                v-model:value="permission.accessLevel"
                :options="accessLevelOptions"
                placeholder="Access Level"
                style="width: 150px"
              />
              <n-button
                type="error"
                size="small"
                quaternary
                @click="removePermission(index)"
              >
                Remove
              </n-button>
            </div>

            <div class="permission-details" v-if="permission.type">
              <!-- SPECIFIC_BRANDS -->
              <n-form-item
                v-if="permission.type === 'SPECIFIC_BRANDS'"
                label="Brand IDs"
              >
                <n-select
                  v-model:value="permission.brandIds"
                  multiple
                  filterable
                  :options="brandOptions"
                  placeholder="Select brands"
                  tag
                />
              </n-form-item>

              <!-- ROLES -->
              <n-form-item
                v-if="permission.type === 'ROLES'"
                label="Roles"
              >
                <n-select
                  v-model:value="permission.roles"
                  multiple
                  :options="roleOptions"
                  placeholder="Select roles"
                />
              </n-form-item>

              <!-- USERS -->
              <n-form-item
                v-if="permission.type === 'USERS'"
                label="User IDs"
              >
                <n-input
                  v-model:value="permission.userIdsText"
                  type="textarea"
                  placeholder="Enter user IDs, one per line"
                  :rows="3"
                  @update:value="updateUserIds(permission, $event)"
                />
              </n-form-item>
            </div>
          </n-card>
        </div>

        <n-empty v-if="permissionsForm.permissions.length === 0" description="No permissions set">
          <template #extra>
            <n-button size="small" @click="addPermission">Add Permission</n-button>
          </template>
        </n-empty>
      </div>

      <div class="permission-actions">
        <n-button @click="addPermission">Add Permission</n-button>
      </div>
    </n-form>

    <template v-if="$slots.default">
      <slot></slot>
    </template>
    <div v-else class="editor-actions">
      <n-button @click="handleCancel">Cancel</n-button>
      <n-button type="primary" @click="handleSave" :loading="saving">
        Save Permissions
      </n-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import {
  NForm,
  NFormItem,
  NSelect,
  NButton,
  NCard,
  NDivider,
  NInput,
  NTag,
  NEmpty,
  useMessage,
} from 'naive-ui'
import { apiClient } from '../services/api'

interface Gallery {
  id: string
  name: string
  description?: string
  ownerBrandId?: string
  isPrivate: boolean
  isDefault: boolean
  permissions: any[]
  accessLevel: 'READ' | 'WRITE' | 'ADMIN'
}

interface Permission {
  type: 'ALL_BRANDS' | 'SPECIFIC_BRANDS' | 'ROLES' | 'ADMIN_ONLY' | 'USERS' | ''
  brandIds?: string[]
  roles?: string[]
  userIds?: string[]
  userIdsText?: string
  accessLevel: 'READ' | 'WRITE' | 'ADMIN'
}

const props = defineProps<{
  gallery: Gallery
}>()

const emit = defineEmits<{
  (e: 'save', permissions: Permission[]): void
  (e: 'cancel'): void
}>()

const message = useMessage()
const saving = ref(false)

const permissionsForm = ref<{ permissions: Permission[] }>({
  permissions: [],
})

const permissionTypeOptions = [
  { label: 'All Brands', value: 'ALL_BRANDS' },
  { label: 'Specific Brands', value: 'SPECIFIC_BRANDS' },
  { label: 'Roles', value: 'ROLES' },
  { label: 'Admin Only', value: 'ADMIN_ONLY' },
  { label: 'Specific Users', value: 'USERS' },
]

const accessLevelOptions = [
  { label: 'Read', value: 'READ' },
  { label: 'Write', value: 'WRITE' },
  { label: 'Admin', value: 'ADMIN' },
]

const roleOptions = [
  { label: 'Admin', value: 'admin' },
  { label: 'Moderator', value: 'moderator' },
  { label: 'User', value: 'user' },
]

const brands = ref<Array<{ id: string; name: string; code: string }>>([])

const brandOptions = computed(() => {
  return brands.value.map(b => ({
    label: `${b.name} (${b.code})`,
    value: b.id,
  }))
})

const loadBrands = async () => {
  try {
    const response = await apiClient.getBrands()
    brands.value = response.brands
  } catch (error: any) {
    console.error('Failed to load brands:', error)
  }
}

const addPermission = () => {
  permissionsForm.value.permissions.push({
    type: '',
    accessLevel: 'READ',
    brandIds: [],
    roles: [],
    userIds: [],
    userIdsText: '',
  })
}

const removePermission = (index: number) => {
  permissionsForm.value.permissions.splice(index, 1)
}

const updateUserIds = (permission: Permission, text: string) => {
  permission.userIdsText = text
  permission.userIds = text
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
}

const handleSave = async () => {
  // Validate permissions
  const validPermissions = permissionsForm.value.permissions
    .filter(p => p.type && p.accessLevel)
    .map(p => {
      const perm: any = {
        type: p.type,
        accessLevel: p.accessLevel,
      }

      if (p.type === 'SPECIFIC_BRANDS' && p.brandIds && p.brandIds.length > 0) {
        perm.brandIds = p.brandIds
      }
      if (p.type === 'ROLES' && p.roles && p.roles.length > 0) {
        perm.roles = p.roles
      }
      if (p.type === 'USERS' && p.userIds && p.userIds.length > 0) {
        perm.userIds = p.userIds
      }

      return perm
    })

  if (validPermissions.length === 0 && props.gallery.isPrivate) {
    message.warning('Private galleries require at least one permission rule')
    return
  }

  saving.value = true
  try {
    await apiClient.updateGalleryPermissions(props.gallery.id, validPermissions)
    message.success('Permissions updated successfully')
    emit('save', validPermissions)
  } catch (error: any) {
    message.error(`Failed to update permissions: ${error.message || 'Unknown error'}`)
  } finally {
    saving.value = false
  }
}

const handleCancel = () => {
  emit('cancel')
}

const initPermissions = () => {
  if (props.gallery.permissions && props.gallery.permissions.length > 0) {
    permissionsForm.value.permissions = props.gallery.permissions.map((p: any) => ({
      type: p.type,
      accessLevel: p.accessLevel,
      brandIds: p.brandIds || [],
      roles: p.roles || [],
      userIds: p.userIds || [],
      userIdsText: p.userIds ? p.userIds.join('\n') : '',
    }))
  } else {
    permissionsForm.value.permissions = []
  }
}

onMounted(async () => {
  await loadBrands()
  initPermissions()
})
</script>

<style scoped>
.gallery-permission-editor {
  padding: 16px 0;
}

.gallery-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.permissions-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.permission-item {
  width: 100%;
}

.permission-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.permission-details {
  margin-top: 12px;
}

.permission-actions {
  margin-top: 16px;
}

.editor-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid var(--border-color);
}
</style>
